import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { X, Keyboard, AlertCircle, RotateCcw } from "lucide-react";

/**
 * Reusable camera-based QR scanner modal. The printed cards only show the QR
 * artwork — there's no human-readable code on them — so anywhere the admin
 * needs to identify a specific card (assigning it to a student, adding it to
 * the pool, etc.) should scan it rather than ask for typed input. A manual
 * text-entry fallback is still offered inside the modal for when the camera
 * is unavailable or the card is damaged/unreadable.
 */
export default function QRScannerModal({ title = "Scan QR Code", onDetected, onClose }) {
    const [scanning, setScanning] = useState(false);
    const [camError, setCamError] = useState("");
    const [manualOpen, setManualOpen] = useState(false);
    const [manualToken, setManualToken] = useState("");
    const scannerRef = useRef(null);
    const regionId = useRef(`qr-scan-region-${Math.random().toString(36).slice(2)}`).current;

    const stopScanner = async () => {
        if (scannerRef.current) {
            try {
                await scannerRef.current.stop();
                scannerRef.current.clear();
            } catch {
                // ignore
            }
            scannerRef.current = null;
        }
        setScanning(false);
    };

    const startScanner = async () => {
        setCamError("");
        // In case a previous attempt left a half-started instance around (e.g.
        // the user hit Retry after an error), make sure it's torn down first —
        // acquiring the camera while a prior handle still holds it is exactly
        // what causes NotReadableError below.
        await stopScanner();

        if (!window.isSecureContext) {
            setCamError("Camera requires HTTPS. Use the secure deployment URL.");
            setManualOpen(true);
            return;
        }
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            setCamError("Camera API not supported in this browser.");
            setManualOpen(true);
            return;
        }

        let cameraConfig = { facingMode: "environment" };
        try {
            const devices = await Html5Qrcode.getCameras();
            if (devices && devices.length) {
                const back =
                    devices.find((d) => /back|rear|environment/i.test(d.label)) || devices[devices.length - 1];
                cameraConfig = back.id;
            }
        } catch {
            // fall back to facingMode
        }

        const qrboxFn = (w, h) => {
            const edge = Math.min(w, h, 280);
            const size = Math.max(160, Math.floor(edge * 0.75));
            return { width: size, height: size };
        };

        // "NotReadableError: Could not start video source" is usually a *transient*
        // OS/driver-level lock (the previous holder — another tab, or our own
        // last-used stream — hasn't fully released the device yet), not a real
        // permanent conflict. Retry a couple of times with a short backoff before
        // surfacing an error, instead of failing on the very first attempt.
        const maxAttempts = 3;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                const scanner = new Html5Qrcode(regionId, { verbose: false });
                scannerRef.current = scanner;
                await scanner.start(
                    cameraConfig,
                    { fps: 10, qrbox: qrboxFn, aspectRatio: 1.0 },
                    async (decoded) => {
                        await stopScanner();
                        onDetected(decoded);
                    },
                    () => {}
                );
                setScanning(true);
                return;
            } catch (err) {
                scannerRef.current = null;
                const msg = err?.message || String(err);
                const busy = /NotReadableError|Could not start video source/i.test(msg);
                if (busy && attempt < maxAttempts) {
                    await new Promise((r) => setTimeout(r, attempt * 500));
                    continue;
                }
                if (/NotAllowedError|Permission denied/i.test(msg)) {
                    setCamError("Camera permission denied. Please allow camera access and retry.");
                } else if (busy) {
                    setCamError(
                        "Camera is busy — another app or browser tab may be using it. Close it and tap Retry."
                    );
                } else if (/NotFoundError|no camera|Requested device not found/i.test(msg)) {
                    setCamError("No camera found on this device.");
                } else {
                    setCamError(`Unable to start camera: ${msg}`);
                }
                setManualOpen(true);
                return;
            }
        }
    };

    useEffect(() => {
        startScanner();
        return () => {
            stopScanner();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submitManual = (e) => {
        e.preventDefault();
        if (!manualToken.trim()) return;
        onDetected(manualToken.trim());
    };

    const handleClose = async () => {
        await stopScanner();
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4">
            <div className="bg-white border-2 border-[#111111] w-full max-w-sm animate-slide-up">
                <div className="flex justify-between items-center px-6 py-4 border-b-2 border-[#111111]">
                    <h3 className="font-display text-2xl">{title}</h3>
                    <button onClick={handleClose} data-testid="close-scanner" className="p-1 hover:bg-[#F8F9FA]">
                        <X size={20} />
                    </button>
                </div>
                <div className="p-6">
                    <div className="relative bg-black aspect-square w-full overflow-hidden mb-4">
                        <div id={regionId} className="absolute inset-0" />
                        {!scanning && !camError && (
                            <div className="absolute inset-0 flex items-center justify-center text-white/60 text-sm">
                                Starting camera…
                            </div>
                        )}
                    </div>
                    {camError && (
                        <div
                            data-testid="scanner-camera-error"
                            className="mb-4 bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] px-4 py-3 text-sm flex items-start gap-2"
                        >
                            <AlertCircle size={16} className="mt-0.5 shrink-0" />
                            <div className="flex-1">
                                <p>{camError}</p>
                                <button
                                    type="button"
                                    onClick={startScanner}
                                    data-testid="retry-camera"
                                    className="mt-2 inline-flex items-center gap-1 font-bold underline"
                                >
                                    <RotateCcw size={12} /> Retry camera
                                </button>
                            </div>
                        </div>
                    )}
                    <p className="text-xs text-[#666666] mb-3 text-center">
                        Hold the printed QR card steady in front of the camera.
                    </p>
                    {!manualOpen ? (
                        <button
                            type="button"
                            onClick={() => setManualOpen(true)}
                            data-testid="scanner-manual-toggle"
                            className="w-full text-[#666666] text-sm flex items-center justify-center gap-2 hover:text-[#111111]"
                        >
                            <Keyboard size={14} /> Enter code manually instead
                        </button>
                    ) : (
                        <form onSubmit={submitManual} className="flex gap-2">
                            <input
                                autoFocus
                                data-testid="scanner-manual-input"
                                value={manualToken}
                                onChange={(e) => setManualToken(e.target.value)}
                                placeholder="SCX-…"
                                className="flex-1 bg-[#F8F9FA] border border-[#E5E5E5] px-3 py-2 font-mono text-sm focus:border-[#111111] outline-none"
                            />
                            <button
                                type="submit"
                                data-testid="scanner-manual-submit"
                                className="bg-[#111111] text-white font-bold px-4 text-sm"
                            >
                                Use
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
